import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import type { BillingBudgetCreate } from '../../types/billing';
import { useBillingBusinessUnitOptions } from '../../hooks/useBillingBusinessUnitOptions';

export interface AddBillingAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: BillingBudgetCreate) => Promise<void>;
}

type FieldKey =
  | 'account_id'
  | 'account_name'
  | 'cloud_type'
  | 'currency'
  | 'conversion_rate'
  | 'business_unit'
  | 'sub_bu'
  | 'bu_head_email'
  | 'sbu_head_email'
  | 'owner_email'
  | 'annual_fixed_budget'
  | 'annual_budget_revised_month'
  | 'status';

interface FieldDef {
  key: FieldKey;
  label: string;
  required?: boolean;
  numeric?: boolean;
}

// Excluded by design: monthly budget/spend, annual remaining, saved, YTD spend,
// YTD %, % of total — none are user-settable here.
// business_unit and sub_bu are rendered separately below as dropdowns, not TextFields.
const FIELDS: FieldDef[] = [
  { key: 'account_id', label: 'Billing Account', required: true },
  { key: 'account_name', label: 'Account Name', required: true },
  { key: 'cloud_type', label: 'Cloud Type', required: true },
  { key: 'currency', label: 'Currency', required: true },
  { key: 'conversion_rate', label: 'FX Rate', required: true, numeric: true },
  { key: 'bu_head_email', label: 'BU Head Email' },
  { key: 'sbu_head_email', label: 'SBU Head Email' },
  { key: 'owner_email', label: 'Owner Email' },
  { key: 'annual_fixed_budget', label: 'Annual Fixed Budget ($)', numeric: true },
  { key: 'annual_budget_revised_month', label: 'Budget Revised Month' },
  { key: 'status', label: 'Status' },
];

const REQUIRED_KEYS = FIELDS.filter((f) => f.required).map((f) => f.key);
const ALL_KEYS: FieldKey[] = [...FIELDS.map((f) => f.key), 'business_unit', 'sub_bu'];

function emptyValues(): Record<FieldKey, string> {
  return Object.fromEntries(ALL_KEYS.map((k) => [k, ''])) as Record<FieldKey, string>;
}

export function AddBillingAccountDialog({
  open,
  onClose,
  onSubmit,
}: AddBillingAccountDialogProps): React.ReactElement {
  const [values, setValues] = useState<Record<FieldKey, string>>(emptyValues);
  const [isFlagged, setIsFlagged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { businessUnitNames, getSubBuNames } = useBillingBusinessUnitOptions(open);

  const reset = () => {
    setValues(emptyValues());
    setIsFlagged(false);
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const missingRequired = useMemo(
    () => REQUIRED_KEYS.filter((k) => values[k].trim() === ''),
    [values],
  );

  const fxInvalid = useMemo(() => {
    const raw = values.conversion_rate.trim();
    if (raw === '') return false; // emptiness handled by missingRequired
    return Number.isNaN(Number(raw));
  }, [values.conversion_rate]);

  const annualInvalid = useMemo(() => {
    const raw = values.annual_fixed_budget.trim();
    return raw !== '' && Number.isNaN(Number(raw));
  }, [values.annual_fixed_budget]);

  const canSubmit = missingRequired.length === 0 && !fxInvalid && !annualInvalid && !submitting;

  const setField = (key: FieldKey, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const text = (k: FieldKey): string | undefined => {
      const v = values[k].trim();
      return v === '' ? undefined : v;
    };
    const num = (k: FieldKey): number | undefined => {
      const v = values[k].trim();
      return v === '' ? undefined : Number(v);
    };

    const payload: BillingBudgetCreate = {
      account_id: values.account_id.trim(),
      account_name: values.account_name.trim(),
      cloud_type: values.cloud_type.trim(),
      currency: values.currency.trim(),
      conversion_rate: Number(values.conversion_rate.trim()),
      business_unit: text('business_unit'),
      sub_bu: text('sub_bu'),
      bu_head_email: text('bu_head_email'),
      sbu_head_email: text('sbu_head_email'),
      owner_email: text('owner_email'),
      annual_fixed_budget: num('annual_fixed_budget'),
      annual_budget_revised_month: text('annual_budget_revised_month'),
      status: text('status'),
      is_flagged: isFlagged,
    };

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(payload);
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Billing Account</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Spend, monthly budgets, and derived totals are managed automatically and
          cannot be set here.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Autocomplete
            options={businessUnitNames}
            value={values.business_unit || null}
            onChange={(_e, newValue) => {
              setField('business_unit', newValue ?? '');
              // Clear sub_bu if it no longer belongs to the newly selected BU.
              if (values.sub_bu && !getSubBuNames(newValue).includes(values.sub_bu)) {
                setField('sub_bu', '');
              }
            }}
            renderInput={(params) => <TextField {...params} label="Business Unit" size="small" />}
            size="small"
            fullWidth
          />
          <Autocomplete
            options={getSubBuNames(values.business_unit || null)}
            value={values.sub_bu || null}
            onChange={(_e, newValue) => setField('sub_bu', newValue ?? '')}
            renderInput={(params) => <TextField {...params} label="Sub-BU" size="small" />}
            size="small"
            fullWidth
          />
          {FIELDS.map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              required={f.required}
              value={values[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              error={
                (f.key === 'conversion_rate' && fxInvalid) ||
                (f.key === 'annual_fixed_budget' && annualInvalid)
              }
              helperText={
                f.key === 'conversion_rate' && fxInvalid
                  ? 'Enter a valid number'
                  : f.key === 'annual_fixed_budget' && annualInvalid
                    ? 'Enter a valid number'
                    : undefined
              }
              type={f.numeric ? 'number' : 'text'}
              size="small"
              fullWidth
            />
          ))}
        </Box>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={isFlagged}
              onChange={(e) => setIsFlagged(e.target.checked)}
              size="small"
            />
          }
          label="Flagged"
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Add Account
        </Button>
      </DialogActions>
    </Dialog>
  );
}
